import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import emailjs from '@emailjs/browser';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { translations } from '../../translations';
import './Contact.css';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const createSchema = (t) =>
  z.object({
    name: z.string().min(1, t.nameRequired || 'Name is required'),
    email: z
      .string()
      .min(1, t.emailRequired || 'Email is required')
      .email(t.emailInvalid || 'Invalid email address'),
    message: z.string().min(1, t.messageRequired || 'Message is required'),
  });

const Contact = ({ language = 'en' }) => {
  const t = translations[language]?.contact || translations.en.contact;
  const schema = createSchema(t);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const onSubmit = async (data) => {
    setStatus(null);
    const formattedMessage = [
      `Name: ${data.name}`,
      `Email: ${data.email}`,
      `Message: ${data.message}`,
      `Locale: ${language}`,
      `Timestamp: ${new Date().toLocaleString()}`,
    ].join('\n');

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: data.name,
          time: new Date().toLocaleString(),
          message: formattedMessage,
        },
        PUBLIC_KEY
      );
      setStatus('success');
      reset();
    } catch {
      setStatus('error');
    }
  };

  const locations = [
    { address: '1350 RENE-LEVESQUE OUEST, H3G 1T4', lat: 45.4975, lng: -73.5795, mapsQuery: '1350+Rene-Levesque+Ouest+Montreal+QC+H3G+1T4' },
    { address: '600 BLVD. DE MAISONNEUVE OUEST, H3A 3J2', lat: 45.5038, lng: -73.5747, mapsQuery: '600+Blvd+De+Maisonneuve+Ouest+Montreal+QC+H3A+3J2' },
    { address: '2001 AVENUE MCGILL COLLEGE, H3A 1G1', lat: 45.5035, lng: -73.5712, mapsQuery: '2001+Avenue+McGill+College+Montreal+QC+H3A+1G1' },
    { address: '2001 BLVD ROBERT-BOURASSA, H3A 2A6', lat: 45.5020, lng: -73.5685, mapsQuery: '2001+Blvd+Robert-Bourassa+Montreal+QC+H3A+2A6' },
    { address: '1100 RENÉ-LÉVESQUE BLVD W, H3B 4N4', lat: 45.4980, lng: -73.5670, mapsQuery: '1100+Rene-Levesque+Blvd+W+Montreal+QC+H3B+4N4' },
    { address: '3575 PARK AVE, MONTREAL, QC H2X 3P9', lat: 45.5155, lng: -73.5745, mapsQuery: '3575+Park+Ave+Montreal+QC+H2X+3P9' },
    { address: '1010 Rue De la Gauchetière O, Montréal, QC H3B 2N2', lat: 45.4980, lng: -73.5660, mapsQuery: '1010+Rue+De+la+Gauchetiere+O+Montreal+QC+H3B+2N2' },
  ];

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current).setView([45.5035, -73.5720], 14);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const redIcon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    locations.forEach((loc) => {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${loc.mapsQuery}`;
      L.marker([loc.lat, loc.lng], { icon: redIcon })
        .addTo(map)
        .on('click', () => { window.open(mapsUrl, '_blank'); });
    });

    const bounds = L.latLngBounds(locations.map((loc) => [loc.lat, loc.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="contact" id="C">
      <div className="contact__container">
        <div className="contact__header">
          <h2 className="contact__main-title">{t.heading}</h2>
          <div className="contact__divider"></div>
        </div>
        
        <div className="contact__content">
          <div className="contact__info">
            <div className="contact__info-card">
              <div className="contact__info-icon">
                <svg viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
                  <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"/>
                </svg>
              </div>
              <div className="contact__info-content">
                <h3 className="contact__title">{t.companyName}</h3>
                <p className="contact__label">{t.locationsLabel}</p>
                <ul className="contact__list">
                  {locations.map((location, index) => (
                    <li key={index}
                      className="contact__location-item"
                      onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${location.mapsQuery}`, '_blank')}
                      role="link"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') window.open(`https://www.google.com/maps/search/?api=1&query=${location.mapsQuery}`, '_blank'); }}
                    >
                      <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"/>
                      </svg>
                      <p className="contact__location-link">{location.address}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="contact__details-row">
              <div className="contact__detail-card">
                <div className="contact__detail-icon">
                  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                    <path d="M493.4 24.6l-104-24c-11.3-2.6-22.9 3.3-27.5 13.9l-48 112c-4.2 9.8-1.4 21.3 6.9 28l60.6 49.6c-36 76.7-98.9 140.5-177.2 177.2l-49.6-60.6c-6.8-8.3-18.2-11.1-28-6.9l-112 48C3.9 366.5-2 378.1.6 389.4l24 104C27.1 504.2 36.7 512 48 512c256.1 0 464-207.5 464-464 0-11.2-7.7-20.9-18.6-23.4z"/>
                  </svg>
                </div>
                <div>
                  <p className="contact__detail-label">{t.callUs}</p>
                  <a href="tel:+14383642535" className="contact__detail-value">+1 438-364-2535</a>
                </div>
              </div>

              <div className="contact__detail-card">
                <div className="contact__detail-icon">
                  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                    <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z"/>
                  </svg>
                </div>
                <div>
                  <p className="contact__detail-label">{t.emailUs}</p>
                  <a href="mailto:instacarspa@gmail.com" className="contact__detail-value">instacarspa@gmail.com</a>
                </div>
              </div>
            </div>

            <div className="contact__social">
              <a href="https://www.instagram.com/instacarspa" target="_blank" rel="noopener noreferrer" className="contact__social-link">
                <svg viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"/>
                </svg>
                Instagram
              </a>
              <a href="https://www.facebook.com/instacarspa" target="_blank" rel="noopener noreferrer" className="contact__social-link">
                <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                  <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"/>
                </svg>
                Facebook
              </a>
            </div>
          </div>

          <div className="contact__form-wrapper">
            <h3 className="contact__form-title">{t.formTitle}</h3>
            <form className="contact__form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="contact__input-group">
                <input
                  type="text"
                  placeholder={t.namePlaceholder}
                  {...register('name')}
                  className={`contact__input ${errors.name ? 'contact__input--error' : ''}`}
                  aria-invalid={errors.name ? 'true' : 'false'}
                />
                {errors.name && <span className="contact__error">{errors.name.message}</span>}
              </div>
              <div className="contact__input-group">
                <input
                  type="email"
                  placeholder={t.emailPlaceholder}
                  {...register('email')}
                  className={`contact__input ${errors.email ? 'contact__input--error' : ''}`}
                  aria-invalid={errors.email ? 'true' : 'false'}
                />
                {errors.email && <span className="contact__error">{errors.email.message}</span>}
              </div>
              <div className="contact__input-group">
                <textarea
                  placeholder={t.messagePlaceholder}
                  rows="5"
                  {...register('message')}
                  className={`contact__textarea ${errors.message ? 'contact__input--error' : ''}`}
                  aria-invalid={errors.message ? 'true' : 'false'}
                />
                {errors.message && <span className="contact__error">{errors.message.message}</span>}
              </div>

              {status === 'success' && (
                <p className="contact__status contact__status--success">{t.thankYou}</p>
              )}
              {status === 'error' && (
                <p className="contact__status contact__status--error">
                  {t.errorMessage || 'Something went wrong. Please try again.'}
                </p>
              )}

              <button type="submit" className="contact__submit" disabled={isSubmitting}>
                {isSubmitting ? (t.sending || 'Sending...') : t.submitButton}
                {!isSubmitting && (
                  <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                    <path d="M476 3.2L12.5 270.6c-18.1 10.4-15.8 35.6 2.2 43.2L121 358.4l287.3-253.2c5.5-4.9 13.3 2.6 8.6 8.3L176 407v80.5c0 23.6 28.5 32.9 42.5 15.8L282 426l124.6 52.2c14.2 6 30.4-2.9 33-18.2l72-432C515 7.8 493.3-6.8 476 3.2z"/>
                  </svg>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Map with all 7 locations as red markers */}
      <div className="contact__map">
        <div ref={mapRef} style={{ width: '100%', height: '450px' }} />
      </div>
    </section>
  );
};

export default Contact;
